import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { MapPin, Storefront, Users, CurrencyInr, TrendUp, ChartLine, PaperPlaneTilt } from "@phosphor-icons/react";

function inr(n) {
  if (n == null) return "—";
  const v = Number(n) || 0;
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(1)}Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(1)}k`;
  return `₹${Math.round(v).toLocaleString("en-IN")}`;
}

export default function IndiaMapDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/india-map")
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="bg-white border border-brand-border rounded-2xl p-6 text-center text-brand-textMuted" data-testid="map-loading">
      Loading economics…
    </div>
  );
  if (!data) return null;

  const t = data.totals || {};

  return (
    <div className="bg-white border border-brand-border rounded-2xl overflow-hidden" data-testid="india-map">
      {/* Header */}
      <div className="p-5 border-b border-brand-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MapPin size={20} weight="duotone" className="text-brand-primary" />
          <div>
            <h3 className="font-heading text-xl font-bold">India economics</h3>
            <p className="text-xs text-brand-textMuted">
              {data.scope === "region"
                ? "Your region's pulse."
                : `${data.regions.length} active region${data.regions.length === 1 ? "" : "s"} · updated live`}
            </p>
          </div>
        </div>
      </div>

      {/* Top strip — the numbers that matter most */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 divide-x divide-y md:divide-y-0 divide-brand-border/60">
        <BigStat testid="tot-active-subs"
          icon={Storefront} label="Active subs"
          value={t.active_subs} hint={`${t.trial_vendors || 0} on trial · ${t.vendors || 0} total`} />
        <BigStat testid="tot-customers"
          icon={Users} label="Customers"
          value={t.customers} hint={`${t.messages || 0} messages sent`} />
        <BigStat testid="tot-revenue-mtd"
          icon={CurrencyInr} label="MTD revenue"
          value={inr(t.revenue_mtd)} hint={`All-time ${inr(t.revenue_all_time)}`} />
        <BigStat testid="tot-next-month"
          icon={ChartLine} label="Next month"
          value={inr(t.next_month_projection)}
          hint="if all active renew" positive />
        <BigStat testid="tot-arr"
          icon={TrendUp} label="ARR run-rate"
          value={inr(t.arr)}
          hint="active × recurring × 12" positive />
        <BigStat testid="tot-arpu"
          icon={PaperPlaneTilt} label="ARPU"
          value={t.active_subs ? inr(t.revenue_all_time / t.active_subs) : "—"}
          hint="revenue / active sub" />
      </div>

      {/* Regions table */}
      <div className="border-t border-brand-border">
        <div className="px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-brand-textMuted bg-brand-bg/40">By region</div>
        {data.regions.length === 0 ? (
          <div className="p-8 text-center text-brand-textMuted text-sm">No active regions yet.</div>
        ) : (
          data.regions.map((r) => (
            <RegionRow key={r.slug} r={r} />
          ))
        )}
      </div>
    </div>
  );
}

function BigStat({ icon: Icon, label, value, hint, positive, testid }) {
  return (
    <div className="p-4 sm:p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-brand-textMuted">{label}</div>
        <Icon size={14} weight="duotone" className={positive ? "text-brand-success" : "text-brand-primary"} />
      </div>
      <div className={`font-heading text-2xl font-bold tracking-tight ${positive ? "text-brand-success" : "text-brand-text"}`}>
        {value ?? 0}
      </div>
      {hint && <div className="text-[11px] text-brand-textMuted mt-1">{hint}</div>}
    </div>
  );
}

function RegionRow({ r }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-brand-border/60" data-testid={`region-row-${r.slug}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full grid grid-cols-12 gap-2 items-center px-5 py-4 hover:bg-brand-bg/40 text-left"
        data-testid={`region-toggle-${r.slug}`}
      >
        <div className="col-span-4 sm:col-span-3">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-brand-primary" />
            <div>
              <div className="font-heading font-bold">{r.name}</div>
              <div className="text-[11px] text-brand-textMuted">{r.state} · ₹{Math.round(r.launch_inr)} → ₹{Math.round(r.recurring_inr)}/mo</div>
            </div>
          </div>
        </div>
        <Cell label="Paying" value={r.active_subs} sub={`${r.trial_vendors} trial`} />
        <Cell label="End customers" value={r.customers} sub={`${r.messages} msgs`} />
        <Cell label="MTD" value={inr(r.revenue_mtd)} sub={`all-time ${inr(r.revenue_all_time)}`} />
        <Cell label="Next month" value={inr(r.next_month_projection)} sub="projection" positive />
        <Cell label="ARR" value={inr(r.arr)} sub="run rate" positive />
        <div className="col-span-12 sm:col-span-1 text-right text-brand-textMuted text-xs">
          {open ? "Hide" : "Cities ›"}
        </div>
      </button>
      {open && (
        <div className="bg-brand-bg/50 px-5 pb-4" data-testid={`region-detail-${r.slug}`}>
          <div className="text-[10px] uppercase tracking-[0.2em] text-brand-textMuted mb-2 pt-2">Cities in {r.name}</div>
          {r.cities.length === 0 ? (
            <div className="text-xs text-brand-textMuted">No signups yet.</div>
          ) : (
            <div className="space-y-1.5">
              {r.cities.map(c => (
                <div key={c.city}
                  className="flex items-center justify-between text-xs bg-white rounded-lg px-4 py-2.5 border border-brand-border/60"
                  data-testid={`city-row-${r.slug}-${c.city}`}>
                  <div className="font-semibold">{c.city}</div>
                  <div className="flex items-center gap-5 text-brand-textMuted">
                    <span><b className="text-brand-text">{c.vendors}</b> vendors</span>
                    <span><b className="text-brand-text">{c.active}</b> paying</span>
                    <span><b className="text-brand-text">{c.customers}</b> end customers</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Cell({ label, value, sub, positive }) {
  return (
    <div className="col-span-4 sm:col-span-2 lg:col-span-1 hidden sm:block">
      <div className="text-[9px] uppercase tracking-[0.15em] text-brand-textMuted">{label}</div>
      <div className={`font-heading text-sm font-bold tracking-tight ${positive ? "text-brand-success" : "text-brand-text"}`}>{value}</div>
      {sub && <div className="text-[10px] text-brand-textMuted">{sub}</div>}
    </div>
  );
}
